import { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { hasPermission, isSuperuser, inScope } from "@/lib/permissions";
import { markActivation, activationKeyForPath } from "@/lib/activation";
import { track } from "@/lib/track";
import { Sparkles, ArrowRight, ArrowLeft, Check, X } from "lucide-react";

const TOUR_KEY = "erio_tour_done_v1";
const TourCtx = createContext(null);
export const useTour = () => useContext(TourCtx) || { startTour: () => {}, running: false };

// Role-aware step list — only stops the user can actually reach are included.
function buildSteps(user) {
  const sup = isSuperuser(user);
  const can = (p) => hasPermission(user, p);
  const steps = [
    {
      route: "/dashboard", selector: '[data-testid="dashboard-tabs"]', placement: "bottom",
      title: "Welcome to ERIO",
      body: "This is your decision-intelligence command centre. Switch between Overview, Financial, Foresight and Intelligence from these tabs.",
    },
    {
      route: "/dashboard", selector: '[data-testid="text-scale-controls"]', placement: "bottom",
      title: "Find the screen too small?",
      body: "Use these A− / A+ buttons in the top nav to enlarge every page. Setting persists per browser. Power-users can also press Ctrl/⌘ + or − for the browser's native zoom on top of this.",
    },
    {
      route: "/dashboard", selector: '[data-testid="theme-heatmap"]', placement: "top",
      title: "Risk posture at a glance",
      body: "The heatmap shades each of the nine risk themes by its highest residual score — your fastest read on where exposure is concentrated today.",
    },
  ];
  if (can("risk.read"))
    steps.push({
      route: "/dashboard", action: '[data-testid="dashboard-tab-foresight"]',
      // Target the tab trigger itself rather than the panel — Radix Tabs
      // keeps inactive panels in the DOM with `hidden=true` so they have a
      // zero-sized bounding rect at the moment we measure them, which made
      // the spotlight invisible. Highlighting the trigger is more direct
      // anyway: it points the eye to "click this".
      selector: '[data-testid="dashboard-tab-foresight"]', placement: "bottom",
      title: "Foresight — where risk is heading",
      body: "Forward-looking analytics: risk velocity, the £ exposure your controls have avoided (counterfactual), and contagion across linked risks and suppliers.",
    });
  if (can("risk.read"))
    steps.push({
      route: "/risks", selector: '[data-testid="risks-intro"]', placement: "bottom",
      title: "The risk register",
      body: "Every enterprise risk, grouped by theme, with inherent/residual scores and the live signals linked to it. Open any risk to quantify its £ exposure and assign a playbook.",
    });
  if (can("supplier.read") && inScope(user, "t_thirdparty"))
    steps.push({
      route: "/suppliers", selector: '[data-testid="supplier-stats"]', placement: "bottom",
      title: "The supply-chain explorer",
      body: "Your n-tier supplier graph with confidence-scored relationships, flagged single-points-of-failure and inline premium-feed enrichment on each supplier.",
    });
  if (can("signal.read"))
    steps.push({
      route: "/signals", selector: '[data-testid="source-filters"]', placement: "bottom",
      title: "Live signals",
      body: "Normalised intelligence from connected sources, grouped by severity. These early-warning inputs link to risks and feed the pre-mortem — the outside world arriving in real time.",
    });
  if (can("attestation.read"))
    steps.push({
      route: "/governance",
      // Point at the attestations list which is always visible near the top
      // of /governance. `evidence-list` only exists when a control is
      // expanded, and `secure-docs-list` is below the fold — both cases
      // gave a zero-sized rect on first paint and made the spotlight vanish.
      selector: '[data-testid="attestations-list"]',
      placement: "top",
      title: "The Trust Centre",
      body: sup
        ? "Attestations, the control evidence vault, and your confidential document library — now grouped and searchable. This is your audit-ready assurance spine."
        : "Attestations and the control evidence vault — the assurance spine that proves your controls hold, with an immutable audit trail.",
    });
  return steps;
}

export const TourProvider = ({ children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [running, setRunning] = useState(false);
  const [steps, setSteps] = useState([]);
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const autoStarted = useRef(false);

  const startTour = useCallback(() => {
    setSteps(buildSteps(user));
    setStepIndex(0);
    setRect(null);
    setRunning(true);
  }, [user]);

  const finish = useCallback((markDone = true) => {
    setRunning(false);
    setRect(null);
    if (markDone) {
      try { localStorage.setItem(TOUR_KEY, "1"); } catch { /* ignore */ }
      markActivation("tour");
    }
  }, []);

  // Auto-start once on the first authenticated session — but ONLY when the user
  // is on the dashboard (their post-login landing). This prevents the tour from
  // hijacking deep links (e.g. /tenants) by navigating them to the first step.
  useEffect(() => {
    if (!user || autoStarted.current) return;
    let done = false;
    try { done = localStorage.getItem(TOUR_KEY) === "1"; } catch { /* ignore */ }
    const path = location.pathname;
    const onDashboard = path === "/dashboard" || path === "/";
    if (!done && onDashboard) {
      autoStarted.current = true;
      const t = setTimeout(() => startTour(), 900);
      return () => clearTimeout(t);
    }
  }, [user, location.pathname, startTour]);

  const step = running ? steps[stepIndex] : null;

  // Record area visits for the Dashboard activation checklist.
  useEffect(() => {
    if (!user) return;
    const k = activationKeyForPath(location.pathname);
    if (k) markActivation(k);
    track("page_view", { path: location.pathname });
  }, [user, location.pathname]);

  // Navigate to the step's route, run any pre-action, then spotlight the target.
  useEffect(() => {
    if (!step) return;
    // Allow Escape to exit the tour (avoids a keyboard focus trap).
    const onKey = (e) => { if (e.key === "Escape") finish(true); };
    window.addEventListener("keydown", onKey);
    let cancelled = false;
    let poll;
    const run = async () => {
      if (location.pathname !== step.route) {
        navigate(step.route);
        return; // effect re-runs after the route changes
      }
      if (step.action) {
        const a = document.querySelector(step.action);
        if (a) {
          a.click();
          await new Promise((r) => setTimeout(r, 350));
        }
      }
      let tries = 0;
      poll = setInterval(() => {
        if (cancelled) { clearInterval(poll); return; }
        const el = document.querySelector(step.selector);
        // Require a non-zero rect — Radix's hidden inactive tab panels
        // and not-yet-rendered async sections will return 0x0 and the
        // spotlight would otherwise be invisible.
        const r = el?.getBoundingClientRect();
        const ready = el && r && r.width > 0 && r.height > 0;
        if (ready) {
          clearInterval(poll);
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          setTimeout(() => { if (!cancelled) setRect(el.getBoundingClientRect()); }, 320);
        } else if (++tries > 24) {
          clearInterval(poll);
          setRect(null); // fallback → centred tooltip
        }
      }, 130);
    };
    run();
    return () => { cancelled = true; if (poll) clearInterval(poll); window.removeEventListener("keydown", onKey); };
  }, [step, location.pathname, navigate]);

  // Keep the spotlight aligned while the user scrolls / resizes.
  useEffect(() => {
    if (!step) return;
    const update = () => {
      const el = document.querySelector(step.selector);
      if (el) setRect(el.getBoundingClientRect());
    };
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [step]);

  const next = () => {
    if (stepIndex >= steps.length - 1) finish(true);
    else { setRect(null); setStepIndex((i) => i + 1); }
  };
  const back = () => {
    if (stepIndex > 0) { setRect(null); setStepIndex((i) => i - 1); }
  };

  return (
    <TourCtx.Provider value={{ startTour, running }}>
      {children}
      {running && step && (
        <TourLayer
          rect={rect}
          step={step}
          index={stepIndex}
          total={steps.length}
          onNext={next}
          onBack={back}
          onClose={() => finish(true)}
        />
      )}
    </TourCtx.Provider>
  );
};

const PAD = 8;
const TIP_W = 340;
const TIP_H = 230;

function TourLayer({ rect, step, index, total, onNext, onBack, onClose }) {
  const last = index === total - 1;

  // Anchor the tooltip in ONE consistent position (bottom-centre) for every step
  // so the card no longer jumps around the screen — the moving spotlight ring
  // alone draws the eye to the relevant element. Far more professional.
  const tipStyle = { bottom: 28, left: "50%", transform: "translateX(-50%)", width: TIP_W };

  return createPortal(
    <div className="fixed inset-0 z-[9998]" data-testid="tour-overlay">
      {rect ? (
        <div
          className="pointer-events-none fixed rounded-lg transition-all duration-300"
          style={{
            top: rect.top - PAD,
            left: rect.left - PAD,
            width: rect.width + PAD * 2,
            height: rect.height + PAD * 2,
            boxShadow: "0 0 0 9999px rgba(15,12,30,0.66)",
            border: "2px solid rgba(119,55,246,0.95)",
          }}
        />
      ) : (
        <div className="pointer-events-none fixed inset-0" style={{ background: "rgba(15,12,30,0.66)" }} />
      )}

      <div
        data-testid="tour-tooltip"
        className="fixed z-[9999] max-w-[calc(100vw-32px)] rounded-2xl border border-ek-purple/20 bg-white p-5 shadow-2xl"
        style={tipStyle}
      >
        <button
          onClick={onClose}
          aria-label="Close tour"
          data-testid="tour-close"
          className="absolute right-3 top-3 rounded-md p-1 text-ek-ink/35 transition-colors hover:bg-ek-ink/5 hover:text-ek-ink"
        >
          <X size={15} />
        </button>
        <div className="flex items-center gap-1.5 text-ek-purple">
          <Sparkles size={14} />
          <span className="text-[11px] font-bold uppercase tracking-wide">
            Guided tour · {index + 1} / {total}
          </span>
        </div>
        <h3 className="mt-2 font-display text-lg font-semibold text-ek-ink">{step.title}</h3>
        <p className="mt-1.5 text-sm leading-relaxed text-ek-ink/65">{step.body}</p>
        <div className="mt-4 flex items-center justify-between">
          <button
            onClick={onClose}
            data-testid="tour-skip"
            className="text-xs font-medium text-ek-ink/65 transition-colors hover:text-ek-ink"
          >
            Skip tour
          </button>
          <div className="flex items-center gap-2">
            {index > 0 && (
              <button onClick={onBack} data-testid="tour-back" className="ek-btn-outline flex items-center gap-1 !px-3 !py-1.5 text-xs">
                <ArrowLeft size={13} /> Back
              </button>
            )}
            <button onClick={onNext} data-testid="tour-next" className="ek-btn flex items-center gap-1 !px-3 !py-1.5 text-xs">
              {last ? <>Finish <Check size={13} /></> : <>Next <ArrowRight size={13} /></>}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
